<script setup>
/**
 * ImmersiveBackdrop
 * The immersive screen's ambient signature: a heavily blurred, dimmed copy of
 * the CURRENT product's image behind all content, so every item tints the room
 * with its own atmosphere. Purely decorative: aria-hidden, untouchable, and
 * deliberately subtle — the product pane stays the hero.
 *
 * Engineering contract (iOS Safari perf):
 *   - EXACTLY two fixed <img> layers (A/B). Opacity is the ONLY animated
 *     property; filter/transform stay frozen so the expensive 40px blur is
 *     rasterized once per image, never per frame.
 *   - The imageUrl prop is bound to the COMMITTED item (never the live drag),
 *     so the ambience stays calm under the finger and cross-fades exactly once
 *     per real navigation.
 */
import { ref, watch } from 'vue'

const props = defineProps({
  // Same URL as the visible pane image — the browser cache already holds the
  // bytes, so this costs zero extra network.
  imageUrl: { type: String, default: null },
})

const aUrl = ref(null)
const bUrl = ref(null)
// Layer currently faded IN ('a' | 'b' | null); null = the item has no image,
// so BOTH layers fade out and the overlay's flat theme background shows
// through (the designed fallback, not an error state).
const front = ref(null)

function frontUrl() {
  return front.value === 'a' ? aUrl.value : front.value === 'b' ? bUrl.value : null
}

watch(
  () => props.imageUrl,
  (url) => {
    // Same image again (re-render, or two items sharing one photo): no fade —
    // cross-fading identical pixels would only cause a pointless mid-fade dip.
    if (url && url === frontUrl()) return
    if (!url) {
      // Keep the stale URL on the outgoing layer so it fades out still SHOWING
      // the old image, rather than snapping blank before the fade runs.
      front.value = null
      return
    }
    // Load the incoming image into the BACK layer, then flip which is front.
    // CSS transitions opacity on both (out + in) = the cross-fade.
    if (front.value === 'a') {
      bUrl.value = url
      front.value = 'b'
    } else {
      aUrl.value = url
      front.value = 'a'
    }
  },
  { immediate: true }
)
</script>

<template>
  <!-- Pure atmosphere: hidden from the a11y tree, never intercepts touches. -->
  <div class="imm-backdrop" aria-hidden="true">
    <img
      v-if="aUrl"
      :src="aUrl"
      alt=""
      decoding="async"
      class="imm-backdrop-img"
      :class="{ 'is-front': front === 'a' }"
    />
    <img
      v-if="bUrl"
      :src="bUrl"
      alt=""
      decoding="async"
      class="imm-backdrop-img"
      :class="{ 'is-front': front === 'b' }"
    />
    <!-- Contrast scrim, stronger toward the bottom where the identity strip,
         price and quick-add live — they keep full contrast over ANY image. -->
    <div class="imm-backdrop-scrim"></div>
  </div>
</template>

<style scoped>
/* z-index -1 inside the overlay's stacking context: paints above the overlay's
   flat background-color and below its radial-glow ::before/::after (z 0) and
   the frame (z 1) — the brand glow stays composited OVER the ambience. */
.imm-backdrop {
  position: absolute;
  inset: 0;
  z-index: -1;
  overflow: hidden; /* clips the over-scaled image layers to the viewport */
  pointer-events: none;
}

.imm-backdrop-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* STATIC treatment — none of this ever animates:
     - scale(1.18) pushes the blur-softened edges off-screen;
     - translateZ(0) isolates the layer on the compositor so the once-per-image
       blur raster is cached and the swipe track never forces a repaint;
     - the saturation boost keeps the tint reading as COLOR after the heavy
       blur + low opacity would otherwise wash it toward gray. */
  transform: scale(1.18) translateZ(0);
  filter: blur(40px) saturate(1.25);
  opacity: 0;
  /* Opacity-ONLY cross-fade (never filter/transform). */
  transition: opacity 400ms ease;
}

/* LOW opacity by design: the theme base keeps dominating — the image reads as
   ambient tint through frosted glass, never as a picture behind the content.
   .theme-light on the overlay overrides --imm-ambient-opacity. */
.imm-backdrop-img.is-front {
  opacity: var(--imm-ambient-opacity, 0.22);
}

/* Bottom-weighted scrim: the lower half hosts identity/price/quick-add. */
.imm-backdrop-scrim {
  position: absolute;
  inset: 0;
  background: var(
    --imm-ambient-scrim,
    linear-gradient(
      180deg,
      rgba(10, 15, 26, 0.18) 0%,
      rgba(10, 15, 26, 0.42) 55%,
      rgba(10, 15, 26, 0.7) 100%
    )
  );
}

@media (prefers-reduced-motion: reduce) {
  .imm-backdrop-img {
    transition: none;
  }
}
</style>
